import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Action, CognitoState, performLogin } from 'react-cognito';
import Color from 'color';
import sampleSize from 'lodash/sampleSize';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import { EMPTY_ARRAY, EMPTY_OBJECT, RESPONSIVE_SIZE } from '../../constants';
import { refreshLoginIfNeeded } from '../../actions/api';
import { showDownloadClipModal } from '../../actions/clips';
import { hideModal } from '../../actions/modal';
import { clipsFromSceneId } from '../../actions/search';
import { setColors } from '../../actions/ui';
import { imageColors, textColor, themeColors } from '../../lib/Color';
import { getTag, getTagName } from '../../lib/tags';
import history from '../../history';
import Button from '../Button';
import ClipPlayer from '../ClipPlayer';
import ClipThumbnailGrid from '../ClipThumbnailGrid';
import Link from '../Link';
import Section from '../Section';
import SecondaryAction from '../SecondaryAction';
import Style from '../Style';
import { H2, H3, H4, H6, L2, L4, T1, T2 } from '../Typography';

const SHORT_TAG_CHARS = 80;
const SHORT_KEYWORD_CHARS = 145;
const SHORT_WORD_PAD = 10;

class ClipLayout extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    clip: PropTypes.object.isRequired,
    otherClipsTitle: PropTypes.string,
    onBack: PropTypes.string,
    backToType: PropTypes.string.isRequired,
    backToName: PropTypes.string.isRequired,
    onLicense: PropTypes.func.isRequired,
    colors: PropTypes.object,
    loggedIn: PropTypes.bool,
    updateColors: PropTypes.func,
    loadClips: PropTypes.func,
  };
  static defaultProps = {
    otherClipsTitle: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      otherClips: EMPTY_ARRAY,
      otherClipsLoaded: false,
      shortTags: '',
      longTags: undefined,
      showLongTags: false,
      shortKeywords: '',
      longKeywords: undefined,
      showLongKeywords: false,
    };
  }

  componentWillMount() {
    this.processNewProps(EMPTY_OBJECT, this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.processNewProps(this.props, nextProps);
  }

  processNewProps = (props, nextProps) => {
    if (props.clip === nextProps.clip) {
      return;
    }

    let stateUpdate = {
      otherCLips: EMPTY_ARRAY,
      otherClipsLoaded: false,
      shortTags: '',
      longTags: undefined,
      showLongTags: false,
      shortKeywords: '',
      longKeywords: undefined,
      showLongKeywords: false,
    };

    if (nextProps.clip) {
      nextProps.updateColors(themeColors(nextProps.clip.scene_colors));

      const tags = nextProps.clip.tags.map(getTag).filter(Boolean);
      const longTags = tags
        .map(t => getTagName(t.id))
        .sort()
        .join(', ');
      stateUpdate.shortTags = trimWords(
        longTags,
        SHORT_TAG_CHARS,
        SHORT_WORD_PAD,
      );
      stateUpdate.longTags =
        longTags === stateUpdate.shortTags ? undefined : longTags;

      const longKeywords = (nextProps.clip.keywords || EMPTY_ARRAY).join(', ');
      stateUpdate.shortKeywords = trimWords(
        longKeywords,
        SHORT_KEYWORD_CHARS,
        SHORT_WORD_PAD,
      );
      stateUpdate.longKeywords =
        longKeywords === stateUpdate.shortKeywords ? undefined : longKeywords;

      nextProps
        .loadClips(nextProps.clip.scene_id)
        .then(clips =>
          clips.filter(
            otherClip => otherClip.clip_id !== nextProps.clip.clip_id,
          ),
        )
        .then(filtered =>
          this.setState({
            otherClips: sampleSize(filtered, 6),
            otherClipsLoaded: true,
          }),
        );
    }

    this.setState(stateUpdate);
  };

  toggleLongTags = () => {
    this.setState({ showLongTags: !this.state.showLongTags });
  };

  toggleLongKeywords = () => {
    this.setState({ showLongKeywords: !this.state.showLongKeywords });
  };

  render() {
    const {
      responsive,
      clip,
      otherClipsTitle,
      onBack,
      backToType,
      backToName,
      onLicense,
      colors,
      loggedIn,
      updateColors,
      loadClips,
      ...otherProps
    } = this.props;

    let showLicenseButton = false;
    let showTags = false;
    let flexWrapMode = 'wrap';
    let sectionBottomAngle = 8;
    let sectionBottomMargin = 0.5;
    let posterPadding = '5rem 2rem 4rem 0.3rem';
    let gridPadding = '0.1rem 1rem 7rem';
    let anchorLeftMargin = '1rem';
    let titleLeftMargin = '1rem';
    let gridMarginTop = '5rem';
    if (responsive >= RESPONSIVE_SIZE.PHONE) {
      gridPadding = '0.1rem 3rem 7rem';
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      showLicenseButton = true;
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      showTags = true;
      flexWrapMode = 'nowrap';
      sectionBottomAngle = 0;
      posterPadding = '6rem 5rem 0 4rem';
      gridPadding = '1rem 5rem 10rem 5.5rem';
      anchorLeftMargin = '1.5rem';
      titleLeftMargin = '3rem';
      gridMarginTop = '9rem';
    }

    const colorTextDark = textColor(colors.dark);
    const clipLength = moment(clip.clip_length).format('m:ss');

    const row = {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      flexWrap: flexWrapMode,
    };
    const leftColumn = {
      flexBasis: '80rem',
      width: '80rem',
    };
    const rightColumn = {
      flexBasis: '45rem',
      width: '45rem',
    };

    return (
      <div>
        <Section
          bottomAngle={sectionBottomAngle}
          bottomMargin={sectionBottomMargin}
          backgroundColor={colors.light}
          contentStyle={{
            padding: posterPadding,
          }}
        >
          <div
            style={{
              maxWidth: '130rem',
              margin: 'auto',
            }}
          >
            <div style={{ ...row }}>
              <div style={leftColumn}>
                <div style={{ marginBottom: '2rem', maxWidth: '80%' }}>
                  <Link
                    style={{
                      display: 'block',
                      marginLeft: anchorLeftMargin,
                      color: Style.Colors.shadow1,
                    }}
                    to={typeof onBack === 'string' ? onBack : undefined}
                    onClick={typeof onBack === 'function' ? onBack : undefined}
                  >
                    <L2>BACK TO {backToType.toUpperCase()}</L2>
                    <T2>{backToName.toUpperCase()}</T2>
                  </Link>
                </div>
              </div>
              <div style={rightColumn} />
            </div>

            <div style={{ ...row, position: 'relative', top: '2rem' }}>
              <div style={leftColumn}>
                <ClipPlayer
                  backgroundColor={imageColors(clip.clip_colors).dark}
                  lqip={clip.clip_lqip}
                  thumbnail={clip.clip_thumbnail}
                  poster={clip.clip_poster}
                  video={clip.clip_preview_large || clip.clip_preview_small}
                  colorA={colors.dark}
                  colorB={colors.highlight}
                />
              </div>
              <div style={rightColumn}>
                <div
                  style={{
                    paddingTop: '7%',
                    marginLeft: titleLeftMargin,
                    marginRight: 'auto',
                    marginBottom: '5vw',
                  }}
                >
                  <L2
                    style={{
                      color: Style.Colors.shadow2,
                      opacity: 0.75,
                      marginBottom: '1rem',
                      display: 'block',
                    }}
                  >
                    CLIP • {clipLength}
                  </L2>
                  <H4 style={{ marginBottom: '3.7rem' }}>{clip.clip_name}</H4>
                  {showLicenseButton && (
                    <div style={{ display: 'inline-block' }}>
                      <Button
                        onClick={() => onLicense(clip)}
                        baseColor={colors.light}
                        size="large"
                        style={{ letterSpacing: '0.2rem' }}
                      >
                        LICENSE&nbsp;CLIP
                      </Button>
                      {loggedIn && (
                        <SecondaryAction
                          target="_self"
                          download={`${clip.clip_name} preview.mp4`}
                          to={clip.clip_preview_large}
                        >
                          Download preview clip
                        </SecondaryAction>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section
          topAngle={8}
          backgroundColor={colors.dark}
          contentStyle={{
            padding: gridPadding,
            color: colorTextDark,
          }}
        >
          <div style={{ maxWidth: '130rem', margin: 'auto' }}>
            {showTags && (
              <div style={{ ...row, paddingTop: '2.75rem' }}>
                <div style={leftColumn}>
                  <div
                    style={{
                      color: colorTextDark,
                      display: 'flex',
                      flexDirection: 'row',
                      margin: '0 -3rem',
                    }}
                  >
                    <div style={{ margin: '0 3rem', flexBasis: '24rem' }}>
                      <H6>Categories</H6>
                      <L2
                        style={{
                          letterSpacing: 0,
                          fontWeight: 'regular',
                          opacity: 0.5,
                        }}
                      >
                        {this.state.showLongTags
                          ? this.state.longTags
                          : this.state.shortTags}{' '}
                        {this.state.longTags &&
                          !this.state.showLongTags && (
                            <Link
                              underline={true}
                              onClick={this.toggleLongTags}
                            >
                              more
                            </Link>
                          )}
                        {this.state.showLongTags && (
                          <Link underline={true} onClick={this.toggleLongTags}>
                            less
                          </Link>
                        )}
                      </L2>
                    </div>
                    <div style={{ margin: '0 3rem', flexBasis: '37rem' }}>
                      <H6>Tags</H6>
                      <L2
                        style={{
                          letterSpacing: 0,
                          fontWeight: 'regular',
                          opacity: 0.5,
                        }}
                      >
                        {this.state.showLongKeywords
                          ? this.state.longKeywords
                          : this.state.shortKeywords}{' '}
                        {this.state.longKeywords &&
                          !this.state.showLongKeywords && (
                            <Link
                              underline={true}
                              onClick={this.toggleLongKeywords}
                            >
                              more
                            </Link>
                          )}
                        {this.state.showLongKeywords && (
                          <Link
                            underline={true}
                            onClick={this.toggleLongKeywords}
                          >
                            less
                          </Link>
                        )}
                      </L2>
                    </div>
                  </div>
                </div>
                <div style={rightColumn} />
              </div>
            )}

            <div
              style={{
                maxWidth: '150rem',
                margin: `${gridMarginTop} auto 0`,
              }}
            >
              <H3 style={{ marginBottom: '1.5rem' }}>{otherClipsTitle}</H3>
              {!this.state.otherClipsLoaded && <span>Loading...</span>}
              {this.state.otherClipsLoaded && (
                <ClipThumbnailGrid
                  clips={this.state.otherClips}
                  colorA={colors.dark}
                  colorB={colors.highlight}
                />
              )}
            </div>
          </div>
        </Section>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.runtime.responsive,
    colors: state.ui.colors,
    loggedIn: state.cognito.state === CognitoState.LOGGED_IN,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onLicense: clip =>
      dispatch(
        showDownloadClipModal(
          clip.clip_id,
          undefined,
          'Scene',
          () => {
            history.push(`/browse/scene/${clip.scene_slug}`);
            setTimeout(() => dispatch(hideModal()), 300);
          },
          'Browse',
          () => {
            history.push(`/browse`);
            setTimeout(() => dispatch(hideModal()), 300);
          },
        ),
      ),
    updateColors: colors => dispatch(setColors(colors)),
    loadClips: sceneId => dispatch(clipsFromSceneId(sceneId)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ClipLayout);

function trimWords(longWords, charLimit, pad) {
  if (longWords.length <= charLimit + pad) {
    return longWords;
  }

  let shortWords = longWords.slice(0, charLimit);
  return `${shortWords}...`;
}



// WEBPACK FOOTER //
// ./src/components/ClipLayout/ClipLayout.js